import {
  SearchResultSchema as PrismaSearchResultSchema,
  SearchRequestSchema as PrismaSearchRequestSchema,
  UserSchema,
} from '@retro-ui/prisma';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  ExportedSearchResultInListSchema,
  PublicSearchResultSchema,
  SearchResultCommonMetadataSchema,
  SearchResultInListSchema,
  SearchResultSchema,
  withPaginationSchema,
} from '../common';
import {
  PublicSearchBasicInfoSchema,
  PublicSearchWithMetadataSchema,
  RunSearchDataSchema,
  SearchCreditOverviewSchema,
  SearchIdSchema,
  SearchQuerySchema,
  SearchResultIdSchema,
  SearchResultQuerySchema,
  SearchStatusSchema,
  SearchWithMetadataSchema,
  SmilesInfoSchema,
  validateTargetMetadata,
} from './search-schema';

const SharedSearchSchema = z.array(UserSchema.shape.id);

const SearchResultsSchema = z.array(
  PublicSearchResultSchema.extend({
    targetSmilesLength: z.number(),
    canonizedSmilesInfo: SmilesInfoSchema,
  }).merge(PrismaSearchResultSchema.pick({ metadata: true }))
);

const c = initContract();
const pathPrefix = '/search';

export const searchContract = c.router(
  {
    fetchSearches: {
      method: 'GET',
      path: '',
      query: SearchQuerySchema,
      responses: {
        200: withPaginationSchema(z.array(SearchWithMetadataSchema)),
      },
    },
    fetchUniqueSearch: {
      method: 'GET',
      path: '/:searchId',
      query: SearchQuerySchema.pick({ where: true }),
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      responses: {
        200: SearchWithMetadataSchema,
      },
    },
    adminFetchSearches: {
      method: 'GET',
      path: '/all-searches',
      query: SearchQuerySchema,
      responses: {
        200: withPaginationSchema(z.array(SearchWithMetadataSchema)),
      },
    },
    adminExportSearches: {
      method: 'GET',
      path: '/all-searches/export',
      query: SearchQuerySchema.extend({
        includeResults: z
          .union([z.boolean(), z.literal('true'), z.literal('false')])
          .transform(value => value === true || value === 'true')
          .optional(),
      }),
      responses: {
        200: z.array(
          z.object({
            search: PublicSearchWithMetadataSchema.extend({ ownerEmail: z.string() }),
            searchResults: SearchResultsSchema.nullable(),
          })
        ),
      },
    },
    adminExportSearchesBasicInfo: {
      method: 'GET',
      path: '/all-searches/export/basic-info',
      query: SearchQuerySchema,
      responses: {
        200: z.array(PublicSearchBasicInfoSchema),
      },
    },
    fetchPublicSearchDetails: {
      method: 'GET',
      path: '/:searchId/details',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      responses: {
        200: z.object({
          search: PublicSearchWithMetadataSchema,
          searchResults: SearchResultsSchema,
        }),
      },
    },
    fetchAnyUniqueSearch: {
      method: 'GET',
      path: '/:searchId/data',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      responses: {
        200: PublicSearchWithMetadataSchema,
      },
    },
    runSearch: {
      method: 'POST',
      path: '',
      body: RunSearchDataSchema.partial()
        .required({ source: true })
        .superRefine(validateTargetMetadata('targetsMetadata')),
      responses: {
        201: PrismaSearchRequestSchema,
      },
    },
    stopSearches: {
      method: 'PATCH',
      path: '/stop-searches',
      query: SearchQuerySchema.pick({ where: true }),
      body: z.array(PrismaSearchRequestSchema.shape.id),
      responses: {
        200: z.array(SearchStatusSchema),
      },
    },
    removeSearches: {
      method: 'DELETE',
      path: '',
      query: SearchQuerySchema.pick({ where: true }),
      body: z.array(PrismaSearchRequestSchema.shape.id),
      responses: {
        200: z.array(PrismaSearchRequestSchema.shape.id),
      },
    },
    fetchSearchResults: {
      method: 'GET',
      path: '/:searchId/results',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      query: SearchResultQuerySchema,
      responses: {
        200: withPaginationSchema(z.array(SearchResultInListSchema.merge(SearchResultCommonMetadataSchema))),
      },
    },
    exportSearchResults: {
      method: 'GET',
      path: '/:searchId/results/export',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      query: SearchResultQuerySchema,
      responses: {
        200: withPaginationSchema(z.array(ExportedSearchResultInListSchema)),
      },
    },
    fetchAllSearchResults: {
      method: 'GET',
      path: '/:searchId/results/all',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      query: SearchResultQuerySchema,
      responses: {
        200: z.array(SearchResultSchema.pick({ id: true, reactionCount: true })),
      },
    },
    fetchUniqueSearchResult: {
      method: 'GET',
      path: '/:searchId/results/:resultId',
      pathParams: z.object({
        searchId: SearchIdSchema,
        resultId: SearchResultIdSchema,
      }),
      responses: {
        200: SearchResultSchema.merge(SearchResultCommonMetadataSchema),
      },
    },
    updateSearchName: {
      method: 'PATCH',
      path: '/:searchId/name',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      body: z.object({ name: z.string() }),
      responses: {
        200: SearchWithMetadataSchema,
      },
    },
    updateSearchPriority: {
      method: 'PATCH',
      path: '/:searchId/priority',
      pathParams: z.object({
        searchId: SearchIdSchema,
      }),
      body: z.object({ priority: z.number().int().gte(1).lte(10) }),
      responses: {
        200: SearchWithMetadataSchema,
      },
    },
    shareSearch: {
      method: 'POST',
      path: '/:searchId/shared',
      pathParams: z.object({
        searchId: PrismaSearchRequestSchema.shape.id,
      }),
      body: SharedSearchSchema,
      responses: {
        200: SharedSearchSchema,
      },
    },
    validateTargets: {
      method: 'POST',
      path: '/function/validate-targets',
      body: z.array(z.string()),
      responses: {
        200: z.record(z.string()),
      },
    },
    fetchCreditOverview: {
      method: 'GET',
      path: '/meta/credit-overview',
      responses: {
        200: SearchCreditOverviewSchema,
      },
    },
  },
  {
    pathPrefix,
    validateResponseOnClient: true,
  }
);
